import {
  Box,
  Button,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { parseSomething } from "../../utils/parseSomething";
import { useDispatch, useSelector } from "react-redux";
import { isValidMotionProp, motion, AnimatePresence } from "framer-motion";
import { COMMON_WEB_PATH } from "../../constant";
import Count from "./components/Count";
import dayjs from "dayjs";
import useSound from "../../hook/useSound";
import {
  CLEAR_SOCKET_MESSAGE,
  notice,
  SAVE_SOCKET_MESSAGE,
} from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import {
  storeCurrencyExchange,
  triggerTransferGivenUpPoint,
  SHOW_MAINTAIN_ALERT,
  TRANSFER_READY,
} from "../../redux/action/member/memberAction";
import earningMusic from "../../images/music/earning-money.mp3";
import useCheckLogin from "../../hook/useCheckLogin";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonAlertModal from "../../components/Common/commonAlertModal";
import EndGameDetail from "../../components/EndGameDetail/EndGameDetail";
import { useTransferInGameHandler } from "../Game/components/TransferInGame/transferInGameHandler";
import { formatNumber } from "../../utils/formatNumber";
import MainBalancePending from "./components/MainBalancePending";
import DevTestPanel from "./components/DevTestPanel";
import { handleRemoteControl } from "../../utils/remoteControl";
// import TestDepositButton from "./components/TestDepositButton";

const gradientStyle = {
  background:
    "linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(247,249,79,1) 0%, rgba(254,238,54,1) 50%, rgba(245,174,12,1) 75%, rgba(236,208,59,1) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const animationVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
    y: -20,
    rotate: -10,
  },
  visible: {
    scale: 1.5,
    opacity: 1,
    y: 0,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 15,
      mass: 0.8,
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94], // cubic-bezier for smooth easing
      when: "beforeChildren",
      staggerChildren: 0.15,
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    y: 20,
    rotate: 5,
    transition: {
      duration: 0.3,
      ease: [0.55, 0.085, 0.68, 0.53], // smooth exit easing
    },
  },
};

const HEARTBEAT_INTERVAL = 30000;
// pending 期間主動補問結果的間隔與上限(後端不一定會主動推送完成)
const TRANSFER_CHECK_INTERVAL = 8000;
const TRANSFER_CHECK_MAX_TIMES = 10;

const EarnMoneyFromSocket = () => {
  const { t } = useTranslation();
  const i18n_common = (key, props) => t(`common.${key}`, { ...props });
  const i18n_backend = (key) => t(`backend_response.${key}`);

  const toast = useToast();

  const [socketUrl, setSocketUrl] = useState(process.env.REACT_APP_WS_HOST);
  const [messages, setMessages] = useState([]);
  const [testWsData, setTestWsData] = useState(null); // 測試用：模擬 WS 訊息
  const messageIdRef = useRef(0);
  const generateMessageId = () => `${Date.now()}-${messageIdRef.current++}`;
  const [giveUpState, setGiveUpState] = useState({
    show: false,
    previousBalance: 0,
    withdrawalNumber: "",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const memberInfo = useSelector((state) => state.isMemberInfo);
  const isSocketMessage = useSelector((state) => state.isSocketMessage);
  const isTransferInGameState = useSelector(
    (state) => state.isTransferInGameState,
  );
  const gameConfig = useSelector((state) => state.isGameConfig);

  const { message, show, rawMessage } = isSocketMessage || {};

  const { handleTransferAllIn } = useTransferInGameHandler();

  const { uid, cagent_uid } = memberInfo || {};

  const currentGameUid = gameConfig?.gameUid ?? null;
  const { isLogin } = useCheckLogin();

  const earningSound = useSound({ customSound: "earning_money" });

  const didUnmount = useRef(false);

  // 記錄已處理過的 ws 訊息時間戳，避免換頁造成依賴變動時同一則訊息被重複處理

  const processedTimeStampRef = useRef(null);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    queryParams: {
      key: "wsky",
    },
    onOpen: () => console.log("連線成功"),
    shouldReconnect: (closeEvent) => {
      /*
      useWebSocket will handle unmounting for you, but this is an example of a 
      case in which you would not want it to automatically reconnect
    */
      return didUnmount.current === false;
    },
    reconnectAttempts: 9999,
    reconnectInterval: 1000,
  });

  const socketReceivedData = useMemo(() => {
    if (lastMessage?.data) {
      return parseSomething(lastMessage.data);
    }
  }, [lastMessage?.timeStamp]);

  const compareMemberData = useMemo(() => {
    if (socketReceivedData?.member_info_uid == uid) {
      return true;
    }
    return false;
  }, [socketReceivedData, uid]);

  const handleConfirm = () => {
    dispatch(CLEAR_SOCKET_MESSAGE());
  };

  const handleCloseMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  // maintain_notice_member 處理:抽出供「真實 socket」與「測試按鈕」共用,確保判斷一致不會 drift
  const handleMaintainNoticeMember = (data, member_info_uid) => {
    if (data?.cagent_uid != null) {
      if (String(cagent_uid) === String(data.cagent_uid)) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data.msg,
            show: true,
            rawMessage: true,
          }),
        );
        setTimeout(() => {
          navigate("/");
        }, 5000);
      }
      return;
    } else if (Number(member_info_uid) === Number(uid)) {
      if (data?.game_id != null) {
        // 對齊集成 API(resolveGameMaintainDisplay):
        // 只有「在 gamebox 且維護的正是當前遊戲(同一平台/遊戲)」才跳出通知並導回首頁;
        // 不在 gamebox 或維護的不是當前遊戲一律靜默,避免在大廳被無關的遊戲維護通知打斷。
        const isInGameBox = window.location.pathname.includes("gamebox");
        const isCurrentGameMaintaining =
          Array.isArray(data.game_id) &&
          data.game_id.some((id) => String(id) === String(currentGameUid));
        if (isInGameBox && isCurrentGameMaintaining) {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: data.msg,
              show: true,
              rawMessage: true,
            }),
          );
          setTimeout(() => {
            navigate("/");
          }, 5000);
        }
        return;
      }

      dispatch(
        SAVE_SOCKET_MESSAGE({
          message: data,
          show: true,
        }),
      );
      setTimeout(() => {
        window.location.reload();
      }, 50000);
    }
  };

  const responseSocketModal = useMemo(() => {
    if (show) {
      return (
        <CommonAlertModal
          openModal={show}
          setOpenModal={handleConfirm}
          priorityContent={rawMessage ? message : i18n_backend(message)}
        />
      );
    }
  }, [show, message]);

  const responseComponents = (
    <Box
      position="fixed"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Flex direction={"column"} gap="10px" alignItems={"center"}>
        <AnimatePresence>
          {messages?.map((msg) => {
            const { amount = 0, id } = msg;
            return (
              <ChakraBox
                key={id}
                variants={animationVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                bg={"rgba(0,0,0,0.5)"}
                px="30px"
                py="10px"
                borderRadius={"common"}
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                bgPosition="center"
                position="relative"
              >
                <Box
                  as="button"
                  position="absolute"
                  top="6px"
                  right="10px"
                  color="white"
                  fontSize="20px"
                  lineHeight="1"
                  cursor="pointer"
                  onClick={() => handleCloseMessage(id)}
                  zIndex={1}
                >
                  ✕
                </Box>
                <Flex
                  gap="10px"
                  alignItems="center"
                  fontSize="90px"
                  fontWeight={900}
                  {...gradientStyle}
                >
                  <Text>+</Text>
                  <Text>
                    {/* <Count from={0} to={Number(amount)} /> */}
                    {Number(amount)}
                  </Text>
                </Flex>
              </ChakraBox>
            );
          })}
        </AnimatePresence>
      </Flex>{" "}
    </Box>
  );

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    if (socketReceivedData) {
      // 每則 ws 訊息只處理一次；換頁造成的依賴變動不應重跑同一則(避免上分動畫重複)
      if (lastMessage?.timeStamp === processedTimeStampRef.current) return;
      processedTimeStampRef.current = lastMessage?.timeStamp;
      processSocketMessage(socketReceivedData);
    }
  }, [socketReceivedData, uid]);

  // socket 訊息處理:抽出供「真實 socket」與「測試面板」共用,測到即真實邏輯,不會 drift
  const processSocketMessage = (parsed) => {
    const { type, member_info_uid, data, memo } = parsed || {};
    /* 後台遠端關鎖 / 解鎖機台(黑屏),見 utils/remoteControl.js */
    if (handleRemoteControl(parsed, memberInfo?.machine_number)) return;
    if (type === "deposit" && member_info_uid === uid && data != null) {
        const { amount, exchange } = data || {};
        if (amount == "error") {
          return dispatch(
            notice({
              title: i18n_backend("Server Error"),
              type: "error",
            }),
          );
        }
        dispatch(storeCurrencyExchange(exchange || {}));

        /**
         * @description 當收到存款(入鈔)的訊息時，會觸發 all-in-la API 將現金轉進遊戲
         */
        handleTransferAllIn();

        new Audio(earningMusic).play();

        const messageWithId = {
          amount: amount,
          id: generateMessageId(),
        };
        setMessages((prev) => [...prev, messageWithId]);

        setTimeout(() => {
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== messageWithId.id),
          );
        }, 4000);
      } else if (
        type === "transCode" &&
        member_info_uid === uid &&
        data != null
      ) {
        if (data == "fails") {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: memo,
              show: true,
            }),
          );
        } else {
          dispatch(CLEAR_SOCKET_MESSAGE());
          dispatch(triggerTransferGivenUpPoint(true));

          setTimeout(() => {
            //RESET
            dispatch(triggerTransferGivenUpPoint(false));
          }, 5000);
        }
      } else if (type === "error" && member_info_uid === uid && data != null) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data,
            show: true,
          }),
        );
      } else if (type === "update_member") {
        if (isLogin) {
          dispatch({ type: "GET_MEMBER_DATA" });
        }
      } else if (type === "maintain_notice") {
        dispatch(
          SHOW_MAINTAIN_ALERT({
            state: true,
            maintainTime: data,
          }),
        );
      } else if (type === "maintain_notice_member") {
        handleMaintainNoticeMember(data, member_info_uid);
      } else if (type == "reload") {
        window.location.reload();
      } else if (type == "transfer_in_game" || type == "CHECK_RREQUEST") {
        const { request_id, amount, message, platform, status } = data || {};
        if (member_info_uid !== uid) return;

        const current_request_id = localStorage.getItem(
          "transfer_in_game_request_id",
        );

        if (status === "pending") {
          localStorage.setItem("transfer_in_game_request_id", request_id);
          return;
        } else {
          if (current_request_id == request_id) {
            if (status === "rollback") {
              handleTransferAllIn();
              return;
            }
            localStorage.removeItem("transfer_in_game_request_id");

            dispatch(TRANSFER_READY());
            toast({
              title: i18n_common("提醒您"),
              description: i18n_common(status, {
                amount: formatNumber(amount),
                platform: platform,
                status: status,
              }),
              status: "success",
              duration: 7000,
              isClosable: true,
              position: "top",
            });
          }
        }
      } else if (type === "mac_giveup") {
        if (Number(member_info_uid) === uid) {
          dispatch({ type: "GET_BALANCE" });
          setGiveUpState({
            show: true,
            previousBalance: data?.balance,
            withdrawalNumber: data?.withdrawal_number,
          });
        }
      } else if (type === "mac_giveup_error") {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data?.message,
            show: true,
          }),
        );
      } else if (type === "update_only_one_member") {
        if (isLogin) {
          if (Number(member_info_uid) === uid) {
            dispatch({ type: "GET_MEMBER_DATA" });
          }
        }
      }
  };

  useEffect(() => {
    const heartbeat = setInterval(() => {
      sendMessage("ping");
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeat);
    };
  }, [sendMessage]);

  useEffect(() => {
    /**
     * @description 處理遊戲中轉點的邏輯，發生於101,102
     * 用戶重整時，將 localStorage 中的轉點請求單號傳給後端，後端依單號回傳結果
     * 結果處理依照 WebSocket 回傳的 type 決定
     */
    const current_request_id = localStorage.getItem(
      "transfer_in_game_request_id",
    );

    if (!(current_request_id && uid && isTransferInGameState === "pending")) {
      return;
    }

    const askResult = () => {
      sendJsonMessage({
        type: "CHECK_RREQUEST",
        request_id: current_request_id,
        member_info_uid: uid,
      });
    };

    // 進入 pending 時先問一次(原本的行為)
    askResult();

    /**
     * 後端不一定會主動推送轉點完成,只靠上面問一次的話,
     * 玩家會一直停在「轉入處理中」直到自己重整頁面才知道錢到了。
     * 這裡在 pending 期間定期補問,拿到結果後 isTransferInGameState 會變動,
     * 這支 effect 重跑並在 cleanup 清掉計時器,所以不會多問。
     */
    let askedTimes = 0;
    const checkTimer = setInterval(() => {
      askedTimes += 1;
      if (askedTimes >= TRANSFER_CHECK_MAX_TIMES) {
        clearInterval(checkTimer);
        return;
      }
      askResult();
    }, TRANSFER_CHECK_INTERVAL);

    return () => clearInterval(checkTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isTransferInGameState]);

  const handleTestDeposit = () => {
    const testAmount = 888;
    const messageWithId = {
      amount: testAmount,
      id: generateMessageId(),
    };
    setMessages((prev) => [...prev, messageWithId]);
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
    }, 4000);
  };

  // 測試用：模擬一筆 WS 存款訊息（走與真實相同的 effect 依賴與去重守衛）
  const handleSimulateWsDeposit = (amount = 321) => {
    setTestWsData({ amount, key: `test-ws-${Date.now()}` });
  };

  // 測試用 effect：沿用本站真實 effect 的依賴 [socketReceivedData, uid]，共用 processedTimeStampRef 守衛
  useEffect(() => {
    if (!testWsData) return;
    if (testWsData.key === processedTimeStampRef.current) return;
    processedTimeStampRef.current = testWsData.key;
    new Audio(earningMusic).play();
    const messageWithId = {
      amount: testWsData.amount,
      id: generateMessageId(),
    };
    setMessages((prev) => [...prev, messageWithId]);
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
    }, 4000);
  }, [testWsData, uid]);

  return (
    <Box zIndex={999999} position={"relative"}>
      {responseComponents}
      <MainBalancePending />
      {responseSocketModal}

      {giveUpState.show && (
        <EndGameDetail
          onClose={() => {
            setGiveUpState({
              show: false,
              previousBalance: 0,
              withdrawalNumber: "",
            });
          }}
          previousBalance={giveUpState.previousBalance}
          transCode={""}
          withdrawalNumber={giveUpState.withdrawalNumber}
        />
      )}

      {/* 統一測試面板:僅測試環境顯示(正式站自動隱藏)*/}
      <DevTestPanel
        uid={uid}
        currentGameUid={currentGameUid}
        onProcess={processSocketMessage}
        onSimDeposit={handleSimulateWsDeposit}
      />
      {/* 測試用：手動觸發存款動畫，需要時再開啟 */}
      {/* <TestDepositButton onTest={handleTestDeposit} onSimWs={handleSimulateWsDeposit} /> */}
    </Box>
  );
};

export default EarnMoneyFromSocket;
